import { useState } from 'react';
import { Combobox } from '@headlessui/react';
import { toast } from 'react-toastify';
import { CheckBadgeIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { FiRefreshCcw } from 'react-icons/fi';

type LocationDropdownProps = {
  label: string;
  options: string[];
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
};

export default function LocationDropdown({
  label,
  options,
  value,
  onChange,
  disabled = false,
}: LocationDropdownProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const filteredOptions =
    query === ''
      ? options
      : options.filter((option) =>
          option.toLowerCase().includes(query.toLowerCase())
        );

  return (
    <div className="space-y-1 px-4">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <Combobox value={value} onChange={onChange}>
        {({ open }) => (
          <div className="relative">
            <Combobox.Button
              as="div"
              onClick={() => {
                if (!open && disabled) {
                  if (label === 'City') {
                    toast.error('Please select a state first');
                  } else if (label === 'Outlet') {
                    toast.error('Please select a city first');
                  }
                }
              }}
              className={`flex items-center justify-between w-full cursor-default overflow-hidden rounded-lg bg-white text-left border border-gray-300 py-2 px-3 shadow-sm focus-within:ring-2 focus-within:ring-[#E90C31]  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`block capitalize ${label === 'Outlet' && 'text-center'} text-sm`}
              >
                {value ||
                  (label === 'Outlet'
                    ? `Closest ${label.toLocaleLowerCase()}`
                    : `Select your ${label.toLowerCase()}`)}
              </span>
              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
            </Combobox.Button>

            {!disabled && open && (
              <Combobox.Options
                className={`absolute z-10 right-0 mt-1 w-4/5 ${label === 'Outlet' && 'w-full'} max-h-60 overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
              >
                <div className="relative py-2 pl-2 pr-4">
                  <input
                    type="text"
                    disabled={disabled}
                    className="w-full max-w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none break-words whitespace-normal"
                    placeholder={`Search ${label.toLowerCase()}`}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiRefreshCcw className="w-4 h-4" />
                  </button>
                </div>

                {filteredOptions.length === 0 ? (
                  <div className="cursor-default select-none px-4 py-2 text-gray-500">
                    Nothing found.
                  </div>
                ) : (
                  filteredOptions.map((option) => (
                    <Combobox.Option
                      key={option}
                      value={option}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-10 pr-4 capitalize ${
                          active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={`block w-full max-w-full break-words whitespace-normal capitalize ${
                              selected ? 'font-medium' : 'font-normal'
                            }`}
                          >
                            {option}
                          </span>
                          {selected && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-crunchies-red">
                              <CheckBadgeIcon className="w-4 h-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            )}
          </div>
        )}
      </Combobox>
    </div>
  );
}
